import React, { FormEvent, useEffect, useState, useRef } from 'react';
import '../../styles/select-hover.css';
import '../../styles/category-select.css';
import '../../styles/dialog-select.css';
import { refreshManager } from '../../services/refreshManager';
import { Dialog, DialogHeader, DialogTitle, DialogDescription, DialogContent } from "../ui/dialog";
import { Button } from "../ui/button";
import { CheckCircle, XCircle } from "lucide-react";
import { 
  EnhancedSelect as Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "../ui/enhanced-select";
import { goodThingsService } from '../../services/goodThings';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../ui/accordion";
import { metricsCache, setManualRefreshTimestamp } from '../../services/metricsCache';
import { employeeNamesService } from '../../services/employeeNames';
import { auth } from '../../config/firebase';
import { toast } from '../ui/use-toast';
import { PersonSelect } from './PersonSelect';
import { SuccessDialog } from './SuccessDialog';
import { RecipientTag } from './RecipientTag';
import { userActivityService } from '../../services/userActivityService';
import { notificationsService } from '../../services/notificationsService';
import { NoteField } from './NoteField';
import { AdditionalPeople } from './AdditionalPeople';
import { RichTextContent } from '../../types/good-things';

interface GoodThingDialogProps {
  onSuccess?: () => void;
  className?: string;
  triggerButton?: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

interface FormData {
  date: string;
  submitter: string;
  category: string;
  person1: string;
  additionalRecipients: string[];
  note: string;
  richNote?: RichTextContent;
}

export function GoodThingDialog({ onSuccess, className, triggerButton, open: externalOpen, onOpenChange }: GoodThingDialogProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const dialogContentRef = useRef<HTMLDivElement>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showRecipientWarning, setShowRecipientWarning] = useState(false);
  const [dropdownData, setDropdownData] = useState<{
    categories: string[];
    people: string[];
    filteredPeople?: string[];
  }>({
    categories: [],
    people: []
  });

  // Refs to track dropdown states
  const selectOpenStatesRef = useRef<{
    person1Open: boolean;
    categoryOpen: boolean;
    additionalPersonOpen: boolean;
  }>({
    person1Open: false,
    categoryOpen: false,
    additionalPersonOpen: false
  });

  const isControlled = externalOpen !== undefined;
  const open = isControlled ? externalOpen : internalOpen;
  
  // Custom handler for dialog open state changes
  const handleOpenChange = (newOpenState: boolean) => {
    // If trying to close the dialog
    if (!newOpenState) {
      // If any dropdown is open
      if (selectOpenStatesRef.current.person1Open || 
          selectOpenStatesRef.current.categoryOpen || 
          selectOpenStatesRef.current.additionalPersonOpen) {
        
        // Reset all dropdown states first
        selectOpenStatesRef.current = {
          person1Open: false,
          categoryOpen: false,
          additionalPersonOpen: false
        };
        
        // Force a small delay to allow dropdowns to close before closing dialog
        setTimeout(() => {
          if (isControlled && onOpenChange) {
            onOpenChange(false);
          } else {
            setInternalOpen(false);
          }
        }, 50);
        
        return;
      }
    }
    
    // Normal flow
    if (isControlled && onOpenChange) {
      onOpenChange(newOpenState);
    } else {
      setInternalOpen(newOpenState);
    }
  };
  
  const setOpen = handleOpenChange;

  // Start/end session and lock/unlock component
  useEffect(() => {
    const componentId = 'good-thing-dialog';
    if (open) {
      refreshManager.startSession();
      refreshManager.lockComponent(componentId);
    } else {
      refreshManager.unlockComponent(componentId);
      // Only end session if success dialog is also closed
      if (!showSuccess) {
        refreshManager.endSession();
      }
    }
    return () => {
      refreshManager.unlockComponent(componentId);
      if (!showSuccess) {
        refreshManager.endSession();
      }
    };
  }, [open, showSuccess]);

  // Listen for close event from success dialog
  useEffect(() => {
    const handleCloseEvent = () => {
      if (setOpen) {
        setOpen(false);
      }
    };

    window.addEventListener('closeAddGoodThingDialog', handleCloseEvent);
    return () => {
      window.removeEventListener('closeAddGoodThingDialog', handleCloseEvent);
    };
  }, [setOpen]);

  // Memoize sorted categories
  const sortedCategories = React.useMemo(() => [...dropdownData.categories].sort(), [dropdownData.categories]);

  // Storage keys
  const STORAGE_KEY = 'goodThingDialogFormData';
  
  // Helper functions for sessionStorage
  const saveFormToStorage = (data: FormData) => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
      console.error('Error saving form data to sessionStorage:', error);
    }
  };
  
  const loadFormFromStorage = (): FormData | null => {
    try {
      const storedData = sessionStorage.getItem(STORAGE_KEY);
      if (storedData) {
        return JSON.parse(storedData);
      }
    } catch (error) {
      console.error('Error loading form data from sessionStorage:', error);
    }
    return null;
  };
  
  const clearFormStorage = () => {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.error('Error clearing form data from sessionStorage:', error);
    }
  };

  // Form state
  const [formData, setFormData] = useState<FormData>({
    date: '',
    submitter: '',
    category: '',
    person1: '',
    additionalRecipients: [],
    note: '',
    richNote: undefined
  });
  
  // State for the additional recipient dropdown
  const [additionalRecipientValue, setAdditionalRecipientValue] = useState<string>('');

  // Save form data to sessionStorage whenever it changes
  useEffect(() => {
    if (formData.submitter) {
      saveFormToStorage(formData);
    }
  }, [formData]);

  useEffect(() => {
    if (open) {
      loadData();
    }
  }, [open]);

  const loadData = async () => {
    try {
      const data = await goodThingsService.getDropdownData();
      setDropdownData(data);

      const storedForm = loadFormFromStorage();
      
      const user = auth.currentUser;
      if (user?.email) {
        const displayName = await employeeNamesService.getNameFromEmail(user.email);
        
        if (displayName) {
          let submitterName = '';
          
          if (data.people.includes(displayName)) {
            submitterName = displayName;
            handleChange('submitter', displayName);
          } else {
            const nameWithPeriod = displayName.endsWith('.') ? displayName : `${displayName}.`;
            const match = data.people.find(p => p === nameWithPeriod);
            if (match) {
              submitterName = match;
              handleChange('submitter', match);
            } else {
              toast({
                title: "Invalid user",
                description: "Your display name is not in the list of valid users.",
                variant: "destructive"
              });
            }
          }
          
          if (submitterName) {
            const nameVariations = [submitterName];
            if (submitterName.endsWith('.')) {
              nameVariations.push(submitterName.slice(0, -1));
            } else {
              nameVariations.push(`${submitterName}.`);
            }
            
            const filteredPeople = data.people.filter(person => 
              !nameVariations.includes(person)
            );
            
            setDropdownData({
              categories: data.categories,
              people: data.people,
              filteredPeople: filteredPeople
            });
          }
        }
      }
      
      if (storedForm) {
        const isValidCategory = data.categories.includes(storedForm.category);
        const isValidPerson = data.people.includes(storedForm.person1);
        const validAdditionalRecipients = storedForm.additionalRecipients.filter(
          person => data.people.includes(person)
        );
        
        setFormData(prev => ({
          ...prev,
          category: isValidCategory ? storedForm.category : prev.category,
          person1: isValidPerson ? storedForm.person1 : prev.person1,
          additionalRecipients: validAdditionalRecipients,
          note: storedForm.note || prev.note
        }));
      }
    } catch (error) {
      console.error('Error loading dropdown data:', error);
    }
  };

  // Validate form data and return recipients array if valid, or null if invalid
  const validateForm = (): string[] | null => {
    if (!formData.submitter || !dropdownData.people.includes(formData.submitter)) {
      toast({
        title: "Invalid submitter",
        description: "Your display name is not in the list of valid users.",
        variant: "destructive"
      });
      return null;
    }

    if (!formData.person1) {
      toast({
        title: "Recipient is required",
        description: "Please select a person to give the good thing to.",
        variant: "destructive"
      });
      return null;
    }

    if (!formData.category) {
      toast({
        title: "Category is required",
        description: "Please select a category for this Good Thing.",
        variant: "destructive"
      });
      return null;
    }

    const allRecipients = [formData.person1, ...formData.additionalRecipients];

    if (allRecipients.some(recipient => recipient === formData.submitter)) {
      toast({
        title: "Nice try! 😉",
        description: "While we love your enthusiasm, you can't give yourself a Good Thing. Let others sing your praises!",
        variant: "destructive"
      });
      return null;
    }

    return allRecipients;
  };

  // Reset form except for submitter
  const resetForm = () => {
    const currentSubmitter = formData.submitter;
    setFormData({
      date: '',
      submitter: currentSubmitter,
      category: '',
      person1: '',
      additionalRecipients: [],
      note: '',
      richNote: undefined
    });
    setAdditionalRecipientValue('');
  };
  
  // Clear all form data including submitter and remove from sessionStorage
  const clearForm = () => {
    setFormData({
      date: '',
      submitter: '',
      category: '',
      person1: '',
      additionalRecipients: [],
      note: '',
      richNote: undefined
    });
    setAdditionalRecipientValue('');
    clearFormStorage();
    
    toast({
      title: "Form Cleared",
      description: "All form data has been cleared",
      variant: "default",
      duration: 3000
    });
    
    loadData();
  };

  // Submit Good Thing with success dialog
  const submitGoodThing = async () => {
    const submissionData = {
      date: new Date().toLocaleDateString('en-CA'),
      submitter: formData.submitter,
      category: formData.category,
      recipients: validateForm() as string[],
      note: formData.note,
      richNote: formData.richNote,
      source: 'app'
    };

    setShowSuccess(true);
    resetForm();
    clearFormStorage();

    try {
      await userActivityService.trackGoodThingSubmission('app');
      await goodThingsService.addGoodThing(submissionData);

      const currentUser = auth.currentUser;
      if (currentUser?.email) {
        const userName = await employeeNamesService.getNameFromEmail(currentUser.email);
        
        if (userName) {
          const nameVariations = [userName];
          if (userName.endsWith('.')) {
            nameVariations.push(userName.slice(0, -1));
          } else {
            nameVariations.push(`${userName}.`);
          }
          
          const isRecipient = submissionData.recipients.some(recipient => 
            nameVariations.includes(recipient)
          );
          
          if (isRecipient) {
            notificationsService.createGoodThingNotification(formData.submitter);
          }
        }
      }

      // Removed immediate sync trigger - entries are already correctly stored in Firestore
      // The scheduled sync (every 6 hours) will handle syncing from Google Sheets
      // This prevents the immediate sync from overwriting rich text entries due to Firestore consistency issues

      setManualRefreshTimestamp();
      goodThingsService.sortedEntriesCache.clear();
      metricsCache.clearAllCaches();
      
      toast({
        title: "Good Thing Added! 🎉",
        description: "Your submission has been saved and is ready to view",
        variant: "success",
        duration: 10000,
      });
    } catch (error) {
      console.error('Error initiating good thing submission:', error);
      toast({
        title: "Error",
        description: "There was a problem submitting your Good Thing. Please try again.",
        variant: "destructive"
      });
    }
  };

  // Handle form submit
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    
// Check if there's a name in the Add Person box that hasn't been added to recipients
    if (additionalRecipientValue.trim()) {
      setShowRecipientWarning(true);
      return;
    }

    const recipients = validateForm();
    if (!recipients) return;
    
    await submitGoodThing();
  };

  // Function to proceed with submission after warning is handled
  const proceedWithSubmission = async () => {
    await submitGoodThing();
  };

  const handleChange = (field: keyof FormData, value: string | string[] | RichTextContent) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };
  
  // Helper function to update additionalRecipients array
  const handleAddRecipient = (name: string) => {
    if (!name) return;
    
    // Normalize the name for comparison
    const normalizedName = name.trim();
    
    if (formData.additionalRecipients.length >= 6) {
      toast({
        title: "Maximum recipients reached",
        description: "You can only add up to 6 additional recipients.",
        variant: "destructive"
      });
      return;
    }
    
    // Check for duplicates with case-insensitive comparison
    const isDuplicate = formData.additionalRecipients.some(recipient => 
      recipient.toLowerCase() === normalizedName.toLowerCase()) || 
      normalizedName.toLowerCase() === formData.person1.toLowerCase();
    if (isDuplicate) {
      toast({
        title: "Duplicate recipient",
        description: "This person is already added as a recipient.",
        variant: "destructive"
      });
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      additionalRecipients: [...prev.additionalRecipients, normalizedName]
    }));
  };
  
  // Helper function to remove a recipient
  const handleRemoveRecipient = (index: number) => {
    setFormData(prev => ({
      ...prev,
      additionalRecipients: prev.additionalRecipients.filter((_, i) => i !== index)
    }));
  };

  // Handle adding the pending recipient without submitting
  const handleAddPendingRecipient = () => {
    if (additionalRecipientValue) {
      handleAddRecipient(additionalRecipientValue);
      setAdditionalRecipientValue('');
    }
    setShowRecipientWarning(false);
  };

  // Handle rejecting the pending recipient and proceeding with form submission
  const handleRejectPendingRecipient = () => {
    setAdditionalRecipientValue('');
    setShowRecipientWarning(false);
    proceedWithSubmission();
  };

  return (
    <>
      <SuccessDialog 
        open={showSuccess} 
        onOpenChange={(open) => {
          setShowSuccess(open);
          if (!open) {
            refreshManager.endSession();
          }
        }}
        onAddAnother={() => {
          const currentSubmitter = formData.submitter;
          setFormData({
            date: '',
            submitter: currentSubmitter,
            category: '',
            person1: '',
            additionalRecipients: [],
            note: ''
          });
          
          setAdditionalRecipientValue('');
          clearFormStorage();
          setShowSuccess(false);
          if (setOpen) {
            setOpen(true);
          }
        }}
      />
      <Dialog 
        open={open}
        onOpenChange={setOpen}
      >
        {triggerButton !== null && (
          triggerButton || (
            <Button 
              onClick={() => setOpen?.(true)} 
              className={`bg-pink-600 hover:bg-pink-700 ${className}`}
            >
              Add Good Thing
            </Button>
          )
        )}
        <DialogContent ref={dialogContentRef} className={`good-thing-dialog w-[90vw] sm:max-w-[800px] ${showRecipientWarning ? 'max-h-[90vh]' : 'max-h-[85vh]'} overflow-y-auto dark:bg-gray-800 dark:border-gray-700`}>
          <DialogHeader>
            <DialogTitle className="dark:text-white">Add a Good Thing</DialogTitle>
            <DialogDescription className="dark:text-gray-400">
              Fill out the form below to add a new good thing.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-2">
            <div className="grid grid-cols-3 gap-6">
              <div className="col-span-1 space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium dark:text-gray-200">From</label>
                  <div className="p-2 bg-gray-50 dark:bg-gray-700 rounded-md border dark:border-gray-600 text-sm dark:text-gray-300">
                    {formData.submitter || 'Loading...'}
                  </div>
                </div>
                <PersonSelect
                  value={formData.person1}
                  onValueChange={(value) => handleChange('person1', value)}
                  portalContainer={dialogContentRef.current}
                  people={dropdownData.filteredPeople || dropdownData.people}
                  label="To"
                  onOpenChange={(open) => {
                    selectOpenStatesRef.current.person1Open = open;
                  }}
                />
                <div className="space-y-2">
                  <label className="text-sm font-medium dark:text-gray-200">Good Thing Category</label>
                  <Select
                    value={formData.category}
                    onValueChange={(value: string) => handleChange('category', value)}
                    onOpenChange={(open) => {
                      selectOpenStatesRef.current.categoryOpen = open;
                    }}
                    required
                  >
                    <SelectTrigger className="category-select-trigger dark:bg-gray-700 dark:border-gray-600 dark:text-white">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent portalContainer={dialogContentRef.current} className="select-content-with-hover">
                      {sortedCategories.map((category) => (
                        <SelectItem key={category} value={category}>
                          {category}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="col-span-2 space-y-4">
                <NoteField
                  value={formData.note}
                  onChange={(value) => handleChange('note', value)}
                  onRichTextChange={(richText) => handleChange('richNote', richText)}
                  richTextValue={formData.richNote}
                />
              </div>
            </div>

            <AdditionalPeople
              value={formData.additionalRecipients}
              dropdownValue={additionalRecipientValue}
              onDropdownValueChange={setAdditionalRecipientValue}
              onAdd={handleAddRecipient}
              onRemove={handleRemoveRecipient}
              people={dropdownData.filteredPeople || dropdownData.people}
              portalContainer={dialogContentRef.current}
              onOpenChange={(open) => {
                selectOpenStatesRef.current.additionalPersonOpen = open;
              }}
            />

            {/* Warning message for unselected recipient */}
            {showRecipientWarning && (
              <div className="mt-4 p-4 border border-yellow-400 rounded-md bg-yellow-50 dark:bg-yellow-900/20 dark:border-yellow-700 dark:text-white">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-yellow-800 dark:text-yellow-200">
                    Did you want to add <strong>{additionalRecipientValue}</strong> as a Selected Recipient?
                  </p>
                  <div className="flex space-x-2">
                    <Button
                      type="button"
                      onClick={handleAddPendingRecipient}
                      className="bg-green-600 hover:bg-green-700 text-white p-1 h-8 w-8 rounded-full"
                    >
                      <CheckCircle className="h-5 w-5" />
                    </Button>
                    <Button
                      type="button"
                      onClick={handleRejectPendingRecipient}
                      className="bg-red-600 hover:bg-red-700 text-white p-1 h-8 w-8 rounded-full"
                    >
                      <XCircle className="h-5 w-5" />
                    </Button>
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-end space-x-2">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setOpen?.(false)}
                className="dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:hover:bg-gray-600"
              >
                Cancel
              </Button>
              <Button 
                type="button" 
                variant="secondary" 
                onClick={clearForm}
                className="bg-gray-200 hover:bg-gray-300 dark:bg-gray-600 dark:hover:bg-gray-500 dark:text-white"
              >
                Clear
              </Button>
              <Button type="submit" className="bg-pink-600 hover:bg-pink-700">
                Submit
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default GoodThingDialog;
